import { eventTypeLabel } from "./event-types";
import { eventCardHref } from "./inquiry";
import type { DaySlot, VenueChoice } from "./tasting";

/** A house venue the tasting form can check for clashes. ΑΛΛΟ is a free-text place and is never checked. */
export type HouseChoice = Exclude<VenueChoice, "other">;
export type ConflictKind = "closed" | "interest";

/** One clash on one date at the chosen house, already merged across events and tasting cards. */
export type VenueConflict = {
  event_id: string;
  name: string;
  type_label: string;
  kind: ConflictKind;
  status_label: string;
  slot: DaySlot | null;
  guests: number | null;
  href: string;
};

export type DateConflicts = { date: string; conflicts: VenueConflict[] };

export type ConflictQuery = {
  venue: HouseChoice;
  dates: string[];
  exclude_event: string | null;
  exclude_visit: string | null;
  /** The slot this client chose for each date. A missing or null entry means "any slot": every clash that day is shown. */
  slots?: Record<string, DaySlot | null>;
};

/** One date the form asks about, with the slot chosen for it (null = not chosen yet). */
export type SlotDate = { date: string; slot: DaySlot | null };

/** A raw match from the events table or from an open tasting card's interest dates. */
export type ConflictRow = {
  event_id: string;
  date: string;
  name: string;
  event_type: string | null;
  sales_stage: string;
  slot: DaySlot | null;
  guests: number | null;
};

export const CLOSED_STAGES = ["confirmed", "completed"] as const;
export const IGNORED_STAGES = ["lost", "cancelled"] as const;
const HOUSES: readonly HouseChoice[] = ["casa", "ktema", "regina"];
const MAX_DATES = 8;

export class ConflictQueryError extends Error {}

export function isHouseChoice(value: unknown): value is HouseChoice {
  return typeof value === "string" && (HOUSES as readonly string[]).includes(value);
}

export function conflictKind(stage: string | null | undefined): ConflictKind | null {
  if (!stage || (IGNORED_STAGES as readonly string[]).includes(stage)) return null;
  return (CLOSED_STAGES as readonly string[]).includes(stage) ? "closed" : "interest";
}

export function conflictStatusLabel(kind: ConflictKind): string {
  return kind === "closed" ? "κλεισμένο" : "ενδιαφέρον";
}

export function slotLabel(slot: DaySlot | null | undefined): string | null {
  if (slot === "midday") return "μεσημέρι";
  if (slot === "evening") return "βράδυ";
  return null;
}

/** Reads μεσημέρι/βράδυ from free text such as the workbook service_time ("Βράδυ", "B", "Μ"). */
export function slotFromText(value: string | null | undefined): DaySlot | null {
  const folded = (value ?? "").trim().toLowerCase().normalize("NFD").replace(/\p{M}/gu, "");
  if (!folded) return null;
  if (folded.startsWith("μεσημ") || folded === "μ" || folded === "m") return "midday";
  if (folded.includes("βραδ") || folded.includes("απογευ") || folded === "β" || folded === "b") return "evening";
  return null;
}

export function slotOf(value: unknown): DaySlot | null {
  return value === "midday" || value === "evening" ? value : null;
}

/** Unique valid YYYY-MM-DD dates, in the order the form lists them. */
export function conflictDates(values: (string | null | undefined)[]): string[] {
  const seen = new Set<string>();
  for (const value of values) {
    const date = (value ?? "").trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(date) && !Number.isNaN(Date.parse(`${date}T00:00:00Z`))) seen.add(date);
  }
  return [...seen].slice(0, MAX_DATES);
}

/**
 * Unique dates with their chosen slot, in form order. If the same date is listed twice with
 * different slots, or once without a slot, it counts as "any slot".
 */
export function conflictSlotDates(rows: { date?: string | null; slot?: unknown }[]): SlotDate[] {
  const order: string[] = [];
  const slots = new Map<string, DaySlot | null>();
  for (const row of rows) {
    const [date] = conflictDates([row.date]);
    if (!date) continue;
    const slot = slotOf(row.slot);
    if (!slots.has(date)) {
      order.push(date);
      slots.set(date, slot);
    } else if (slots.get(date) !== slot) {
      slots.set(date, null);
    }
  }
  return order.slice(0, MAX_DATES).map((date) => ({ date, slot: slots.get(date) ?? null }));
}

/** The `dates` query value: "2026-12-05:midday,2026-12-12" (a date without a slot means any slot). */
export function encodeConflictDates(rows: SlotDate[]): string {
  return rows.map((row) => (row.slot ? `${row.date}:${row.slot}` : row.date)).join(",");
}

export function parseConflictQuery(input: { venue?: string | null; dates?: string | null; exclude_event?: string | null; exclude_visit?: string | null }): ConflictQuery {
  const venue = (input.venue ?? "").trim();
  if (!isHouseChoice(venue)) throw new ConflictQueryError("Επίλεξε CASA, ΚΤΗΜΑ ή REGINA.");
  const raw = (input.dates ?? "").split(",");
  if (raw.length > MAX_DATES) throw new ConflictQueryError(`Έως ${MAX_DATES} ημερομηνίες.`);
  const pairs = conflictSlotDates(raw.map((token) => {
    const [date, slot] = token.trim().split(":");
    return { date, slot };
  }));
  const dates = pairs.map((pair) => pair.date);
  const slots: Record<string, DaySlot | null> = {};
  for (const pair of pairs) slots[pair.date] = pair.slot;
  const id = (value: string | null | undefined) => {
    const trimmed = (value ?? "").trim();
    return trimmed && /^[A-Za-z0-9_-]{1,64}$/.test(trimmed) ? trimmed : null;
  };
  return { venue, dates, exclude_event: id(input.exclude_event), exclude_visit: id(input.exclude_visit), slots };
}

/**
 * Merges matches into one line per event and date. A closed event wins over an interest
 * from the same event, and the first known slot and guest count are kept.
 * Closed events come first, then interests, then by name.
 */
export function mergeConflicts(dates: string[], rows: ConflictRow[], excludeEvent: string | null = null, slots: Record<string, DaySlot | null> = {}): DateConflicts[] {
  const byDate = new Map<string, Map<string, VenueConflict>>();
  const known = new Map<string, Set<DaySlot>>();
  for (const date of dates) byDate.set(date, new Map());
  for (const row of rows) {
    if (excludeEvent && row.event_id === excludeEvent) continue;
    const bucket = byDate.get(row.date);
    if (!bucket) continue;
    const kind = conflictKind(row.sales_stage);
    if (!kind) continue;
    const seenKey = `${row.date}|${row.event_id}`;
    if (row.slot) known.set(seenKey, (known.get(seenKey) ?? new Set<DaySlot>()).add(row.slot));
    const current = bucket.get(row.event_id);
    if (!current) {
      bucket.set(row.event_id, {
        event_id: row.event_id,
        name: row.name.trim() || "Χωρίς όνομα",
        type_label: eventTypeLabel(row.event_type) || "—",
        kind,
        status_label: conflictStatusLabel(kind),
        slot: row.slot,
        guests: row.guests,
        href: eventCardHref(row.event_id),
      });
      continue;
    }
    if (kind === "closed" && current.kind !== "closed") {
      current.kind = "closed";
      current.status_label = conflictStatusLabel("closed");
    }
    current.slot = current.slot ?? row.slot;
    current.guests = current.guests ?? row.guests;
  }
  const result: DateConflicts[] = [];
  for (const [date, bucket] of byDate) {
    const wanted = slots[date] ?? null;
    if (wanted) {
      // Slot-aware: hide records known to be at the other slot; keep records with no known slot.
      for (const [eventId, conflict] of bucket) {
        const seen = known.get(`${date}|${eventId}`);
        if (!seen?.size) continue;
        if (seen.has(wanted)) conflict.slot = wanted;
        else bucket.delete(eventId);
      }
    }
    const conflicts = [...bucket.values()].sort((left, right) => {
      if (left.kind !== right.kind) return left.kind === "closed" ? -1 : 1;
      return left.name.localeCompare(right.name, "el");
    });
    if (conflicts.length) result.push({ date, conflicts });
  }
  return result;
}

/** "200 άτομα" style comparison text for the warning, with the current client's own count first. */
export function guestComparison(own: number | null | undefined, conflicts: VenueConflict[]): string {
  const mine = own != null && own > 0 ? `${own}` : "—";
  const others = conflicts.map((conflict) => (conflict.guests != null ? `${conflict.guests}` : "—")).join(" / ");
  return `Εσείς: ${mine} άτομα · Άλλοι: ${others}`;
}
